<template>
  <div :class="$style.ssoContainer">
    <button
      :class="[$style.oktaButton, 'type-label-large-emphasized']"
      type="button"
      @click="handleSSOClick"
      :disabled="loading || props.disabled"
    >
      <svg
        :class="$style.oktaIcon"
        viewBox="0 0 24 24"
        fill="currentColor"
      >
        <path
          d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm0 19.2c-3.972 0-7.2-3.228-7.2-7.2S8.028 4.8 12 4.8s7.2 3.228 7.2 7.2-3.228 7.2-7.2 7.2z"
        />
      </svg>
      <span>{{ buttonText }}</span>
    </button>

    <p
      v-if="error"
      :class="[$style.error, 'type-body-small']"
    >
      {{ error }}
    </p>
  </div>
</template>

<script setup lang="ts">
  import { ref, computed } from 'vue'
  import oidcApi from '~/apis/oidcApi'

  const props = defineProps<{
    orgId?: string
    email?: string
    autoDetected?: boolean
    disabled?: boolean
  }>()

  const loading = ref(false)
  const error = ref('')

  const buttonText = computed(() => {
    if (loading.value) return 'Redirecting...'
    if (props.autoDetected) return 'Sign in with Okta (Detected)'
    return 'Sign in with Okta'
  })

  const handleSSOClick = async () => {
    if (!props.orgId) {
      error.value = 'Please enter a valid email address first'
      return
    }

    await initiateSSO(props.orgId)
  }

  const initiateSSO = async (orgId: string) => {
    loading.value = true
    error.value = ''

    try {
      const redirectUri = window.location.origin + '/oidc/callback'
      const { authorizationUrl } = await oidcApi.initiateOIDCLogin(
        orgId,
        redirectUri,
      )
      window.location.href = authorizationUrl
    } catch (err: any) {
      console.error('Error initiating SSO login:', err)
      error.value = 'Failed to initiate SSO login. Please try again.'
      loading.value = false
    }
  }
</script>

<style module>
  .ssoContainer {
    width: 100%;
    margin: var(--space-16) 0;
  }

  .oktaButton {
    width: 100%;
    background: var(--color-interactive-link);
    color: var(--color-text-on-brand);
    display: flex;
    align-items: center;
    justify-content: center;
    gap: var(--space-8);
    padding: var(--space-12);
    border-radius: var(--radius-sm);
    border: none;
    cursor: pointer;
    transition: background 0.2s;
  }

  .oktaButton:hover:not(:disabled) {
    background: var(--color-interactive-link-hover);
  }

  .oktaButton:disabled {
    background: var(--color-interactive-primary-disabled);
    color: var(--color-text-on-brand);
    opacity: 1;
    cursor: not-allowed;
  }

  .oktaIcon {
    width: 24px;
    height: 24px;
  }

  .error {
    color: var(--color-status-error);
    margin: var(--space-16) 0 0 0;
    text-align: center;
  }
</style>
